import type { NbxHubSDK, CloseReason, OpenOptions, InitOptions } from "./types";
import {
  showModal,
  hideModal,
  removeLoader,
  isModalOpen,
  setModalFullscreen,
  resetFullscreenState,
} from "./modal";
import { getIframe } from "./iframe";
import {
  setupMessageListener,
  removeMessageListener,
  sendInitMessage,
  sendCloseRequest,
  postCustomMessage,
} from "./messaging";
import { getState, setState, resetState } from "./state";
import {
  init as initSdk,
  getInitOptions,
  getPreloadedOverlay,
  getPreloadedModal,
  getPreloadedIframe,
  setPendingOpen,
  setCloseHandler,
  resetInit,
} from "./init";

// Re-export types for consumers
export type {
  NbxHubSDK,
  CloseReason,
  NbxMessage,
  InitOptions,
  OpenOptions,
  MachineSnapshot,
  CompanyData,
  UserData,
  UserRole,
  NbxInitPayload,
} from "./types";

const VERSION = "1.0.0";
const CLOSE_TIMEOUT_MS = 60000;

let currentOpenOptions: OpenOptions | null = null;
let closeTimeoutId: ReturnType<typeof setTimeout> | null = null;
let isClosePending = false;

/**
 * Validates required fields for open()
 */
function validateOpenOptions(options: OpenOptions): void {
  // Validate companyData
  if (!options.companyData) {
    throw new Error("companyData is required");
  }
  if (!options.companyData.rut) {
    throw new Error("companyData.rut is required");
  }
  if (options.companyData.sistemaComputacional === undefined) {
    throw new Error("companyData.sistemaComputacional is required");
  }
  if (options.companyData.sistemaComputacionalGlobal === undefined) {
    throw new Error("companyData.sistemaComputacionalGlobal is required");
  }

  // Validate machineSnapshot
  if (!options.machineSnapshot) {
    throw new Error("machineSnapshot is required");
  }
  if (!options.machineSnapshot.sessionId) {
    throw new Error("machineSnapshot.sessionId is required");
  }

  // Validate feature
  if (!options.feature) {
    throw new Error("feature is required");
  }

  // Validate userData
  if (!options.userData) {
    throw new Error("userData is required");
  }
  if (!options.userData.rut) {
    throw new Error("userData.rut is required");
  }
  if (!options.userData.name) {
    throw new Error("userData.name is required");
  }
  if (!options.userData.email) {
    throw new Error("userData.email is required");
  }
  if (!options.userData.role) {
    throw new Error("userData.role is required");
  }
  if (
    options.userData.role !== "ADMIN" &&
    options.userData.role !== "NO_ADMIN"
  ) {
    throw new Error("userData.role must be 'ADMIN' or 'NO_ADMIN'");
  }
}

/**
 * Executes the actual close (hides modal, resets state, fires callback)
 */
function executeClose(reason: CloseReason): void {
  const state = getState();
  if (state !== "open") return;

  // Clear any pending close timeout
  if (closeTimeoutId) {
    clearTimeout(closeTimeoutId);
    closeTimeoutId = null;
  }
  isClosePending = false;

  const overlay = getPreloadedOverlay();
  const modal = getPreloadedModal();

  if (!overlay || !modal) return;

  const options = currentOpenOptions;

  hideModal(overlay, modal).then(() => {
    removeKeyboardListener();
    resetFullscreenState();
    setState("ready");
    currentOpenOptions = null;
    options?.onClose?.(reason);
  });
}

/**
 * Cancels a pending close (iframe vetoed the close)
 */
function cancelClose(): void {
  if (closeTimeoutId) {
    clearTimeout(closeTimeoutId);
    closeTimeoutId = null;
  }
  isClosePending = false;
}

/**
 * Two-phase close: sends NBX_CLOSE_REQUEST to iframe and waits for response.
 * If iframe sends NBX_CLOSE → executeClose.
 * If iframe sends NBX_CLOSE_CANCELLED → cancelClose.
 * If no response within timeout → force close.
 */
function handleClose(reason: CloseReason): void {
  const state = getState();
  if (state !== "open" || isClosePending) return;

  const iframe = getPreloadedIframe();
  if (!iframe) {
    executeClose(reason);
    return;
  }

  isClosePending = true;

  // Send close request to iframe
  sendCloseRequest(iframe);

  // Safety net: force close if iframe doesn't respond
  closeTimeoutId = setTimeout(() => {
    if (isClosePending) {
      console.warn("[NbxHub SDK] Close timeout - forcing close");
      executeClose(reason);
    }
  }, CLOSE_TIMEOUT_MS);
}

// Set close handler for init module
setCloseHandler(() => handleClose("user_action"));

/**
 * Keyboard event handler for Escape key
 */
function handleKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape" && isModalOpen()) {
    handleClose("user_action");
  }
}

/**
 * Adds keyboard listener
 */
function addKeyboardListener(): void {
  document.addEventListener("keydown", handleKeydown);
}

/**
 * Removes keyboard listener
 */
function removeKeyboardListener(): void {
  document.removeEventListener("keydown", handleKeydown);
}

/**
 * Initializes the SDK by preloading the iframe
 */
function init(options: InitOptions = {}): void {
  initSdk(options);
}

/**
 * Opens the NBX Hub modal with user data
 */
function open(options: OpenOptions): void {
  const state = getState();

  // Validate required fields
  try {
    validateOpenOptions(options);
  } catch (error) {
    options.onError?.(error as Error);
    throw error;
  }

  // If not initialized, do implicit init (backward compatible)
  if (state === "unloaded") {
    console.warn("[NbxHub] init() not called. Initializing now (slower).");
    init({
      baseUrl: options.baseUrl,
      onError: options.onError,
    });

    // Queue the open call
    setPendingOpen(options, () => {
      executeOpen(options);
    });
    return;
  }

  // If still loading, queue the open call
  if (state === "loading") {
    setPendingOpen(options, () => {
      executeOpen(options);
    });
    return;
  }

  // If already open, warn
  if (state === "open") {
    console.warn("[NbxHub] Modal is already open");
    return;
  }

  // State is 'ready', proceed with open
  executeOpen(options);
}

/**
 * Executes the open operation (called when state is ready)
 */
function executeOpen(options: OpenOptions): void {
  const overlay = getPreloadedOverlay();
  const modal = getPreloadedModal();
  const iframe = getPreloadedIframe();

  if (!overlay || !modal || !iframe) {
    const error = new Error("SDK not properly initialized");
    options.onError?.(error);
    throw error;
  }

  currentOpenOptions = options;
  setState("open");

  // Remove loader if still present
  removeLoader(modal);

  // Use baseUrl from init options (which already resolved baseUrl || DEFAULT_BASE_URL)
  const initOpts = getInitOptions();
  const baseUrl = initOpts?.baseUrl || import.meta.env.VITE_NBX_HUB_BASE_URL || "http://localhost:4200";
  const origin = new URL(baseUrl).origin;

  // Build payload for init message
  const payload = {
    companyData: options.companyData,
    machineSnapshot: options.machineSnapshot,
    feature: options.feature,
    userData: options.userData,
    ...(options.promotionCode && { promotionCode: options.promotionCode }),
  };

  // Re-setup message listener with open-specific handlers
  setupMessageListener(origin, {
    onReady: () => {
      // Iframe sent NBX_READY again (possibly reloaded), resend NBX_INIT
      console.log(
        "[NbxHub SDK] 📥 Recibido NBX_READY adicional, reenviando NBX_INIT",
      );
      sendInitMessage(iframe, payload);
    },
    onClose: (reason) => {
      executeClose(reason);
    },
    onCloseCancelled: () => {
      cancelClose();
    },
    onMessage: (message) => {
      // Handle navigation for fullscreen transitions
      if (message.type === "NBX_NAVIGATE") {
        const payload = message.payload as { path?: string } | undefined;
        const path = payload?.path;
        if (path === "/midt/checkout" || path === "/midt/purchase") {
          setModalFullscreen(true);
        } else if (path === "/midt") {
          setModalFullscreen(false);
        }
      }
      options.onMessage?.(message);
    },
    onRedirect: (url) => {
      // Close modal and redirect parent window
      console.log(
        "[NbxHub SDK] 📍 Recibido NBX_REDIRECT, redirigiendo a:",
        url,
      );
      handleClose("api_call");
      window.location.href = url;
    },
  });

  console.log("[NbxHub SDK] 📤 Enviando NBX_INIT con payload:", {
    companyData: payload.companyData,
    machineSnapshot: { ...payload.machineSnapshot, sessionId: "***" },
    feature: payload.feature,
    userData: { ...payload.userData, email: "***" },
  });

  sendInitMessage(iframe, payload);

  // Show modal
  showModal(overlay, modal);

  // Add keyboard listener
  addKeyboardListener();

  // Fire onOpen callback
  options.onOpen?.();
}

/**
 * Closes the modal programmatically
 */
function close(): void {
  if (getState() !== "open") {
    console.warn("[NbxHub] No modal to close");
    return;
  }

  handleClose("api_call");
}

/**
 * Sends a message to the iframe
 */
function postMessage(message: unknown): void {
  const iframe = getPreloadedIframe() || getIframe();
  if (!iframe) {
    console.warn("[NbxHub] Cannot send message: no iframe found");
    return;
  }

  postCustomMessage(iframe, message);
}

/**
 * The NBX Hub SDK instance
 */
const NbxHub: NbxHubSDK = {
  init,
  open,
  close,
  postMessage,
  isOpen: isModalOpen,
  get state() {
    return getState();
  },
  version: VERSION,
};

// Attach to window for UMD usage
if (typeof window !== "undefined") {
  window.NbxHub = NbxHub;
}

export { NbxHub };
export default NbxHub;
